import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { useEvent } from '@legacy-providers/event';

import { getCrewMemberOptionsForRegistration } from '@shared/crew-members/endpoints/getCrewMemberOptionsForRegistration';
import { useFetchCrewMember } from '@shared/crew-members/hooks/useFetchCrewMember';
import { Form, useForm } from '@shared/forms/components/Form';
import { SelectField } from '@shared/forms/components/SelectField';
import { TextField } from '@shared/forms/components/TextField';
import ParkingDaysField from '@shared/parking-requests/components/ParkingDaysField/ParkingDaysField';
import { CreateParkingRequestData } from '@shared/parking-requests/endpoints/createParkingRequest';
import { getRegistrationParkingOptions } from '@shared/parking-requests/endpoints/getRegistrationParkingOptions';
import { getRegistrationParkings } from '@shared/parking-requests/endpoints/getRegistrationParkings';
import { getRegistrationOptionsForEvent } from '@shared/registrations/endpoints/getRegistrationOptionsForEvent';
import { useFetchRegistration } from '@shared/registrations/hooks/useFetchRegistration';
import { cn } from '@shared/utils/helpers';

type RegistrationParking = App.Domain.Parking.Data.RegistrationParkingData;
type ParkingRequest = App.Domain.Parking.Data.ParkingRequestData;
type CrewMember = App.Domain.CrewMember.Data.CrewMemberData;

type ParkingRequestFormProps = {
  registrationId?: number | null;
  action: (values: CreateParkingRequestData) => Promise<ParkingRequest>;
  onSave?: (response: ParkingRequest) => void;
  onSaveAndReturn?: () => void;
  onCancel?: () => void;
};

export function CreateParkingRequestForm({
  registrationId,
  action,
  onSave,
  onSaveAndReturn,
  onCancel,
}: ParkingRequestFormProps) {
  const event = useEvent();
  const [registrationParkings, setRegistrationParkings] = useState<null | RegistrationParking[]>(null);

  const { form, fields } = useForm<CreateParkingRequestData>({
    defaults: {
      registrationId,
      quantity: 1,
      days: [],
    },
  });

  useEffect(() => {
    if (form.getValues().registrationId !== registrationId) {
      form.setValue('registrationId', registrationId ?? null);
    }
  }, [registrationId]);

  const selectedRegistrationId = form.watch('registrationId');
  const crewMember = useFetchCrewMember(form.watch('crewMemberId'));

  useEffect(() => {
    form.resetField('crewMemberId');
    form.resetField('parkingId');

    if (selectedRegistrationId) {
      getRegistrationParkings(selectedRegistrationId, { include: ['parking.parkingStickers'] }).then((parkings) => {
        setRegistrationParkings(parkings.data);
      });
    }
  }, [selectedRegistrationId]);

  console.log({ crewMember, selectedRegistrationId });

  const selectedRegistrationParking =
    registrationParkings?.find((registrationParking) => registrationParking.parkingId === form.watch('parkingId')) ||
    null;

  return (
    <Form action={action} onSave={onSave} onSaveAndReturn={onSaveAndReturn} onCancel={onCancel} form={form}>
      <Trans>RequestParkingMsg</Trans>

      {!registrationId && (
        <SelectField
          name={fields.registrationId}
          label="Registration"
          options={(query) => getRegistrationOptionsForEvent(event.id, query)}
          required
        />
      )}
      <fieldset
        key={selectedRegistrationId}
        className={cn('grid gap-6', { 'opacity-50': !selectedRegistrationId })}
        disabled={!selectedRegistrationId}
      >
        <div className="md:grid grid-cols-2 gap-8">
          <SelectField
            name={fields.crewMemberId}
            label="Crew Member"
            options={(query) => getCrewMemberOptionsForRegistration(selectedRegistrationId!, query)}
            required
          />
          <SelectField
            name={fields.parkingId}
            label="Parking"
            options={(query) => getRegistrationParkingOptions(selectedRegistrationId!, query)}
            required
          />
        </div>
        <div className="md:grid grid-cols-3 gap-8">
          <TextField name={fields.quantity} type="number" label="Amount of stickers" />
        </div>
        <ParkingDaysField
          registrationParking={selectedRegistrationParking}
          crewMember={crewMember}
          name={fields.days}
          label="Days"
          required
        />
      </fieldset>
    </Form>
  );
}
